import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  createCommand,
  SKIP_DOM_SELECTION_TAG,
  type LexicalCommand,
} from 'lexical';

import {
  $changeTemplateLinesOpeners,
  $readSelectedTemplateLineIds,
  $readTemplateLinkGraph,
  isSameTemplateLinkTarget,
  listTemplateLinkTargets,
  type TemplateLinkChange,
  type TemplateLinkGraphLine,
  type TemplateLinkOption,
  type TemplateLinkTarget,
} from '../../lib';

// The editor's own reading and writing of links. The readers run inside a
// read of the editor and the writer inside an update, so all three may be
// $-functions.
export type TemplateLinkAdapter = {
  // The lines the selection covers, or the caret's line.
  readSelectedLineIds: () => string[];
  readLinkGraph: () => TemplateLinkGraphLine[];
  changeLinesOpeners: (lineIds: string[], change: TemplateLinkChange) => void;
};

export const TEMPLATE_LINK_ADAPTER: TemplateLinkAdapter = {
  readSelectedLineIds: $readSelectedTemplateLineIds,
  readLinkGraph: $readTemplateLinkGraph,
  changeLinesOpeners: $changeTemplateLinesOpeners,
};

export type TemplateLinkStart = {
  lineIds: string[];
  // The one option of theirs the link made replaces.
  replaced?: TemplateLinkTarget;
};

// Starts linking the lines given, or without them the selection's lines.
export const START_TEMPLATE_LINK_COMMAND: LexicalCommand<
  TemplateLinkStart | undefined
> = createCommand('START_TEMPLATE_LINK_COMMAND');

// Set by the editor's nodes: links read the document through them.
const LINE_SELECTOR = '[data-line-id]';
const OPTION_SELECTOR = '[data-link-block][data-link-value]';
const SECTION_SELECTOR = '[data-section-title]';

export const readTemplateLinkTarget = (
  option: HTMLElement,
): TemplateLinkTarget => ({
  blockId: option.dataset.linkBlock ?? '',
  value: option.dataset.linkValue ?? '',
});

export const findTemplateLinkLines = (root: HTMLElement, lineIds: string[]) =>
  Array.from(root.querySelectorAll<HTMLElement>(LINE_SELECTOR)).filter((line) =>
    lineIds.includes(line.dataset.lineId ?? ''),
  );

// The option of the document an element is, or is inside of.
export const findTemplateLinkOption = (
  root: HTMLElement,
  element: EventTarget | null,
) => {
  const option =
    element instanceof Element
      ? element.closest<HTMLElement>(OPTION_SELECTOR)
      : null;
  return option && root.contains(option) ? option : null;
};

// The options of the document in its order, each named with the title of the
// section heading before it.
export const readTemplateLinkOptions = (
  root: HTMLElement | null,
): TemplateLinkOption[] =>
  Array.from(
    root?.querySelectorAll<HTMLElement>(
      `${SECTION_SELECTOR}, ${OPTION_SELECTOR}`,
    ) ?? [],
  ).reduce<{ sectionTitle: string; options: TemplateLinkOption[] }>(
    (read, element) => {
      if (element.matches(SECTION_SELECTOR)) {
        return { ...read, sectionTitle: (element.textContent ?? '').trim() };
      }
      const target = readTemplateLinkTarget(element);
      const label = (
        element.dataset.linkLabel ||
        element.textContent ||
        ''
      ).trim();
      const isListed = read.options.some((option) =>
        isSameTemplateLinkTarget(option, target),
      );
      return target.blockId && target.value && label && !isListed
        ? {
            ...read,
            options: [
              ...read.options,
              { ...target, label, sectionTitle: read.sectionTitle },
            ],
          }
        : read;
    },
    { sectionTitle: '', options: [] },
  ).options;

// The mark of a linked line edits its link with these: the options the line
// may be linked to (read when asked, from the document as it is then), link
// mode replacing one option of the line, and adding or dropping one option
// beside the others.
export type TemplateLineLinkEditing = {
  readOptions: () => TemplateLinkOption[];
  onReplace: (opener: TemplateLinkTarget) => void;
  onAdd: (opener: TemplateLinkTarget) => void;
  onRemove: (opener: TemplateLinkTarget) => void;
};

export const useTemplateLineLinkEditing = (
  adapter: TemplateLinkAdapter,
  lineId: string,
): TemplateLineLinkEditing => {
  const [editor] = useLexicalComposerContext();

  // The caret stays where it is in the document but is not put back there:
  // that would take the focus out of the pop-up the change is made from and
  // close it.
  const changeOpeners = (change: TemplateLinkChange) =>
    editor.update(() => adapter.changeLinesOpeners([lineId], change), {
      tag: SKIP_DOM_SELECTION_TAG,
    });

  return {
    readOptions: () =>
      editor.read(() =>
        listTemplateLinkTargets({
          graph: adapter.readLinkGraph(),
          lineIds: [lineId],
          options: readTemplateLinkOptions(editor.getRootElement()),
        }),
      ),
    onReplace: (replaced) =>
      editor.dispatchCommand(START_TEMPLATE_LINK_COMMAND, {
        lineIds: [lineId],
        replaced,
      }),
    onAdd: (added) => changeOpeners({ added }),
    onRemove: (removed) => changeOpeners({ removed }),
  };
};
